/**
 * FLY SINGLE MISSION's four screens as widget lists.
 *
 * Geometry comes from the captures: the grid board from
 * `fly-single-mission.png`, the briefing plate from `patrol-briefing.png`, the
 * prose column from `mission-assignment.png`, the roster plate and its four
 * value fields from `flight-assignments.png`. Content comes from the mission
 * the generators produced — the same `MissionSpec` the modern cards read, in
 * the original's presentation instead of a `<dl>`.
 */

import {
  aircraftLabel,
  playerFlightOf,
  REGION_LABELS,
  sunPositionOf,
  type MissionSpec,
} from "@red-opus/engine";
import {
  BRIEFING_ACTION_Y,
  BRIEFING_COLUMN,
  BRIEFING_PLATE,
  BRIEFING_TEXT_X,
  briefingBlock,
  briefingCapacity,
} from "./briefing-plate.ts";
import { PANEL_SHEET } from "../render/panel-chrome.ts";
import { measureText, type FontAtlas } from "../render/text-metrics.ts";
import { MISSION_GRID_BACKGROUND, MISSION_GRID_BOARD, MISSION_GRID_SLOTS, isGridSlotFlyable } from "./mission-grid.ts";
import { boardSlotRect } from "../render/board-chrome.ts";
import { historicPortraitWidgets } from "./historic-portrait.ts";
import type { MissionsModel } from "./missions-model.ts";
import { AERODROME_START_LABELS } from "../../mission/realism-settings.ts";
import {
  ACTION_ROW_Y,
  actionRow,
  boxValues,
  segmentWidgets,
  wrapText,
  type ActionSpec,
  type BoxedValue,
} from "./panel-parts.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

const openCommand = (field: string) => ({ kind: "command" as const, command: `open:${field}` });

const CANCEL_TO_GRID = { kind: "command", command: "grid" } as const;

/** Every mission type the port flies has the background the original names it after. */
const MISSION_BACKDROP: Readonly<Record<string, string>> = {
  "dogfight-ace": "screens/DOGFIGHT.SCR",
  patrol: "screens/PATROL.SCR",
  "escort-recon": "screens/RECON.SCR",
  "balloon-busting": "screens/BALLBUST.SCR",
  "hunt-zeppelin": "screens/DOWNZEP.SCR",
  // Shares the ace duel's backdrop: both grid buttons are the same underlying
  // `DOGFIGHT` mission type in `PS.EXE`'s own type table, and `DOGFIGHT.SCR` is
  // the only dogfight screen the volume carries.
  "dogfight-squadron": "screens/DOGFIGHT.SCR",
};

export function missionBackdrop(catalogId: string | undefined): string | undefined {
  return catalogId ? MISSION_BACKDROP[catalogId] : undefined;
}

export const MISSION_BACKDROPS: readonly string[] = Object.values(MISSION_BACKDROP);

export function missionGridScreen(model: MissionsModel): ShellScreen {
  const widgets: Widget[] = MISSION_GRID_SLOTS.map((slot, index) => ({
    kind: "button",
    id: slot.id,
    rect: boardSlotRect(MISSION_GRID_BOARD, index),
    label: slot.label,
    enabled: isGridSlotFlyable(slot),
    action: { kind: "command", command: `slot:${slot.id}` },
  }));
  return {
    id: "missions/grid",
    chrome: {
      kind: "board",
      background: MISSION_GRID_BACKGROUND,
      board: {
        frame: MISSION_GRID_BOARD.frame,
        title: "FLY SINGLE MISSION",
        ridgeInsets: MISSION_GRID_BOARD.ridgeInsets,
        footerInsets: MISSION_GRID_BOARD.footerInsets,
      },
    },
    widgets: [...widgets, ...(model.dialog ? [] : [])],
    cancel: { kind: "navigate", path: "/" },
  };
}

export function briefingValues(spec: MissionSpec): readonly BoxedValue[] {
  const player = playerFlightOf(spec);
  const enemy = spec.flights.filter((flight) => flight.side === "hostile").at(-1);
  const values: BoxedValue[] = [{ value: aircraftLabel(player.aircraft), action: openCommand("aircraft") }];
  if (enemy) values.push({ value: aircraftLabel(enemy.aircraft), action: openCommand("enemy") });
  values.push(
    { value: REGION_LABELS[spec.region], action: openCommand("region") },
    { value: sunPositionOf(spec.weather), action: openCommand("time") },
    { value: spec.weather.sky, action: openCommand("sky") },
    { value: `${spec.weather.wind}`, action: openCommand("wind") },
  );
  return values.filter((entry) => entry.value !== "");
}

/**
 * The plate's own geometry and its fit-to-box arithmetic live in
 * `briefing-plate.ts`; re-exported here so every existing caller keeps its
 * import path.
 */
export { BRIEFING_PLATE, BRIEFING_ACTION_Y, BRIEFING_COLUMN, BRIEFING_TEXT_X, briefingBlock, briefingCapacity };

/**
 * The values the original boxes in a briefing sentence. All seven of AD-011's
 * and roadmap 11s's fields are clickable — aeroplane, region, and now the sky,
 * winds and time-of-day too (`open:aircraft` / `open:enemy` / `open:region` /
 * `open:sky` / `open:wind` / `open:time`) — exactly like the manual's Mission
 * Setup sentence, which boxes all of them the same way (measured live:
 * `re-sessions/calib/shots/nav-setup.png`, every one of `[four]`,
 * `[Sopwith Pups]`, `[echelon]`, `[regular]`, `[daytime]`, `[light]`,
 * `[partly cloudy]` drawn with the same box). Only `wind` reaches the flown
 * aeroplane (`spec.weather.wind` rides `WEATHER_WIND_SCALE`, AD-007k §10);
 * `sky` and `timeOfDay` stay drawn-but-not-simulated (`WEATHER_IS_COSMETIC`)
 * — pickable because the original lets you pick them, not because a choice
 * there changes anything yet.
 *
 * `time` boxes `sunPositionOf(spec.weather)` rather than the raw
 * `timeOfDay` word: this port's own briefing sentence follows `PS.EXE`'s
 * *briefing* template verbatim (`"The sun is at %i..."`,
 * `docs/game/missions.md`), which is a different screen from the original's
 * Mission Setup Mad-Libs that spells `[daytime]` out — the raw word never
 * appears in this sentence at all, so boxing it would be a click target with
 * nothing visible to click. The sun phrase is a deterministic, one-to-one
 * function of `timeOfDay` (`mission-build.ts`), so boxing it points at
 * exactly the same underlying value; the popup it opens still lists the four
 * canonical words.
 *
 * "Enemy aircraft" is the *last* hostile flight in the spec rather than the
 * first: every generator but Stop a Bombing Raid has exactly one hostile
 * aeroplane type, so it does not matter which end is read, but that raid's
 * own heavies (hostile, inbound) are listed before its escort scouts, and the
 * escort — not the bomber — is what the briefing template actually names.
 */

/** `PARTS.BMP` frame 20 at (0,87), mirrored at (296,87) — the struts the briefing hangs beside its plate. */
function strutWidgets(): Widget[] {
  return [
    { kind: "picture", sheet: PANEL_SHEET, frame: 20, x: 0, y: 87 },
    { kind: "picture", sheet: PANEL_SHEET, frame: 20, x: 296, y: 87 },
  ];
}

/**
 * The briefing footer's third button, which is **two different buttons**
 * (G19, user's call 2026-08-26).
 *
 * `ui-menus.md` reads the original's own string dump two ways in the same
 * paragraph, and the difference is the whole of this function. A generated
 * sortie goes `ACCEPT` → `MISSION ASSIGNMENT` → `FLIGHT ASSIGNMENTS` →
 * `BEGIN MISSION`, because those two screens are where its *changeable*
 * parameters live (AD-011: aircraft, enemy, region, ammunition, formation,
 * flight size, start position). A Historic Encounter has none of them —
 * `HISTORIC.DAT` fixes the aeroplanes, the opposition, the region and the
 * hour, and the only choice the original offers is the seat, which the side
 * picker already took. So its footer is `BEGIN` and it flies: *"a side picker
 * before the standard Realism Panel → Begin flow"*, against the generated
 * missions' explicit *"→ MISSION ASSIGNMENT → FLIGHT ASSIGNMENTS → BEGIN
 * MISSION"*.
 *
 * `model.historic` is the test rather than the slot's own kind because
 * `chooseSlot` clears it on **every** other slot — including the picker itself,
 * before an encounter is chosen — so it is set exactly when the briefing on
 * screen is a historic one. Mission Assignment loses nothing by it: it is still
 * reached by every generated briefing's `ACCEPT`, by `MISSION ASSIGNMENT` on
 * both rosters, and by the career aerodrome's `FLY NEXT MISSION`.
 */
function briefingLaunchAction(model: MissionsModel): ActionSpec[] {
  return model.historic
    ? [{ id: "begin", label: "BEGIN", action: { kind: "command", command: "begin" } }]
    : [{ id: "accept", label: "ACCEPT", action: { kind: "command", command: "assignment" } }];
}

export function missionBriefingScreen(
  model: MissionsModel,
  spec: MissionSpec,
  title: string,
  catalogId: string,
  font: FontAtlas,
  heading: FontAtlas,
): ShellScreen {
  const block = briefingBlock(spec.briefing, briefingValues(spec), font);
  const prose = segmentWidgets(
    block.segments,
    {
      x: BRIEFING_TEXT_X,
      y: block.y + block.narrative.length * block.pitch,
      maxWidth: BRIEFING_COLUMN,
      pitch: block.pitch,
      maxLines: block.maxLines,
    },
    font,
  );
  const narrative: Widget[] =
    block.narrative.length === 0
      ? []
      : [
          {
            kind: "prose",
            block: { lines: block.narrative, x: BRIEFING_TEXT_X, y: block.y, role: "prose", pitch: block.pitch },
          },
        ];
  const actions: ActionSpec[] = [
    { id: "return", label: "RETURN TO MAIN", action: CANCEL_TO_GRID },
    { id: "realism", label: "REALISM PANEL", action: { kind: "command", command: "realism" } },
    ...briefingLaunchAction(model),
  ];
  return {
    id: "missions/briefing",
    chrome: {
      kind: "panel",
      title,
      emblem: model.nation === "german" ? "cross" : "roundel",
      background: missionBackdrop(catalogId),
    },
    widgets: [
      { kind: "plate", rect: BRIEFING_PLATE },
      ...narrative,
      ...prose,
      // Empty for every mission type but the eight Historic Encounters, which
      // are the only ones the original gives a photograph (`historic-portrait.ts`).
      ...historicPortraitWidgets(catalogId, model.nation),
      ...strutWidgets(),
      ...actionRow(actions, heading, BRIEFING_ACTION_Y),
    ],
    cancel: CANCEL_TO_GRID,
  };
}

const ASSIGNMENT_TEXT_X = 13;
const ASSIGNMENT_HEADING_Y = 30;
const ASSIGNMENT_BODY_Y = 50;
/** The capture's paragraph pitch; the engraved field rule sits at +7, so anything tighter would strike the next line. */
const ASSIGNMENT_PITCH = 10;
const ASSIGNMENT_COLUMN = 294;
/**
 * How far below its `y` a body row's ink can reach: FONT3's own `fontHeight` is
 * 8, but a field row also carries the engraved rule, which `widget-ops.ts` draws
 * as a two-pixel pair at `y + 7`.
 */
const ASSIGNMENT_ROW_INK = 9;

/**
 * Rows this sheet has between its first body row and the action row. The
 * capture's pitch is fixed, so the count is fixed too: twelve.
 */
export const ASSIGNMENT_ROWS = Math.floor((ACTION_ROW_Y - ASSIGNMENT_ROW_INK - ASSIGNMENT_BODY_Y) / ASSIGNMENT_PITCH) + 1;

/** What is printed when the mission's own account is longer than the sheet. */
export const ASSIGNMENT_ELLIPSIS = "...";

/**
 * `MISSION ASSIGNMENT`'s prose, in the original's own field order and wording.
 * The generators' last briefing paragraph is the sun/wind/sky line, which the
 * `WEATHER` field restates, so the plan takes everything between the first
 * paragraph and that one.
 *
 * **The sheet is a fixed thirteen rows and the account can be longer.** A
 * Hawker-Meets-His-Match briefing wraps to twenty-one rows here; before this
 * was measured the whole block was emitted anyway, so on 8,345 of the 11,696
 * sorties swept the prose was painted *under* the action row — the buttons are
 * declared last and so draw over it — and ran up to 88px past it, off the
 * 200-row screen entirely. So `SITUATION` and `MISSION PLAN` get whatever rows
 * the fields leave them and are cut with an ellipsis; `WEATHER`, `TIME` and
 * `START` are the mission's actual parameters and are never dropped.
 */
/**
 * Which of the two `START` labels this sortie is actually flying.
 *
 * Manual, `04-gameplay.md`: *"the Mission Assignment screen will tell you"*
 * whether this is one of the missions that *"start out on the ground at your
 * aerodrome."* `playerFlightOf(spec).onGround` is the mission's own answer —
 * not every type honours `START AT YOUR AERODROME` even when the player has it
 * on (roadmap 10a) — so this reads the spec rather than the panel setting.
 * Clicking the value still opens the picker and still moves the setting, which
 * is what the next sortie of a type that honours it will fly.
 */
export function assignmentStartLabel(spec: MissionSpec): string {
  return playerFlightOf(spec).onGround ? AERODROME_START_LABELS.on : AERODROME_START_LABELS.off;
}

export function assignmentLines(spec: MissionSpec, font: FontAtlas): string[] {
  const [situation, ...rest] = spec.briefing;
  const plan = rest.length > 1 ? rest.slice(0, -1) : rest;
  const start = assignmentStartLabel(spec);
  const account = [
    ...wrapText(font, `SITUATION: ${situation ?? ""}`, ASSIGNMENT_COLUMN),
    "",
    "MISSION PLAN:",
    // Joined, then re-flowed to the sheet. A Historic Encounter's account is
    // stored in `HISTORIC.DAT` as short authored runs — *"…and one other British
    // pilot discovered"* / *"five Albatros D.IIs. Hawker and…"* — and wrapping
    // each run on its own honours breaks that are string-pool boundaries, not
    // screen rows. That is the same misreading `REVIEW.DAT` cost the debriefing
    // lane (`lessons.md`, 2026-08-26); here it also wasted rows the sheet has
    // very few of.
    ...wrapText(font, plan.join(" "), ASSIGNMENT_COLUMN),
  ];
  const fields = [
    "",
    ...wrapText(font, `WEATHER: The winds are ${spec.weather.wind}. The sky is ${spec.weather.sky}.`, ASSIGNMENT_COLUMN),
    `TIME: ${spec.weather.timeOfDay}`,
    ...wrapText(font, `START: ${start}`, ASSIGNMENT_COLUMN),
  ];
  return [...fitAccount(account, ASSIGNMENT_ROWS - fields.length), ...fields].slice(0, ASSIGNMENT_ROWS);
}

/** The account, cut to `budget` rows with the last one spent on the ellipsis that says there was more. */
function fitAccount(account: readonly string[], budget: number): string[] {
  if (budget <= 0) return [];
  if (account.length <= budget) return [...account];
  return [...account.slice(0, budget - 1), ASSIGNMENT_ELLIPSIS];
}

/** The engraved rules the capture draws under each field name, one row below its glyphs. */
function assignmentUnderlines(lines: readonly string[], font: FontAtlas): Widget[] {
  return lines.flatMap((line, index) => {
    const colon = line.indexOf(":");
    if (colon < 0 || line.slice(0, colon) !== line.slice(0, colon).toUpperCase()) return [];
    return [
      {
        kind: "underline" as const,
        x: ASSIGNMENT_TEXT_X,
        y: ASSIGNMENT_BODY_Y + index * ASSIGNMENT_PITCH + 7,
        w: measureText(font, line.slice(0, colon)),
      },
    ];
  });
}

/**
 * The command the `START:` row's boxed value opens — the two-row picker in
 * `mission-dialogs.ts`.
 *
 * `PS.EXE` carries `START NEAR THE ACTION` / `START AT YOUR AERODROME` /
 * `CANCEL` as a standalone three-row popup with no title string of its own, in
 * its own cluster at byte 263040, five and a half kilobytes away from this
 * screen's own button run at 257487 — which is `MISSION ASSIGNMENT`, `CANCEL`,
 * `FLIGHT ASSIGNMENTS`, `BEGIN MISSION`, the title and **three** buttons, the
 * same three `mission-assignment.png` shows. So the popup is not this screen's
 * fourth button; the port gave it one (`c07620a`) and the row could not hold
 * it. It opens instead off the sheet's own `START` value, which is AD-011's
 * idiom for a mission parameter — *"clicking on one will bring up a menu with
 * your possible options"* — and costs the row nothing.
 */
const START_POSITION_ACTION = { kind: "command" as const, command: "aerodrome-start-dialog" };

/**
 * The sheet's body, with the `START:` row's value boxed and clickable and
 * every other row plain prose.
 *
 * The start row is always the last line and always exactly one — `START: ` and
 * the longer of the two labels measure 119px in FONT3 against a 294px column,
 * so `wrapText` never splits it, and `assignmentLines` appends the fields
 * after the account it has already cut to fit.
 */
function assignmentBody(lines: readonly string[], startLabel: string, font: FontAtlas): Widget[] {
  const last = lines.length - 1;
  const startRow = lines[last] ?? "";
  const body = lines.slice(0, last);
  const values: BoxedValue[] = [{ value: startLabel, action: START_POSITION_ACTION }];
  return [
    {
      kind: "prose",
      block: { lines: body, x: ASSIGNMENT_TEXT_X, y: ASSIGNMENT_BODY_Y, role: "prose", pitch: ASSIGNMENT_PITCH },
    },
    ...segmentWidgets(
      boxValues(startRow, values),
      {
        x: ASSIGNMENT_TEXT_X,
        y: ASSIGNMENT_BODY_Y + last * ASSIGNMENT_PITCH,
        maxWidth: ASSIGNMENT_COLUMN,
        pitch: ASSIGNMENT_PITCH,
        maxLines: 1,
      },
      font,
    ),
  ];
}

export function missionAssignmentScreen(
  spec: MissionSpec,
  font: FontAtlas,
  heading: FontAtlas,
  actions: readonly ActionSpec[],
): ShellScreen {
  const title = `REFER TO THE MAP OF THE ${REGION_LABELS[spec.region].toUpperCase()} REGION`;
  const lines = assignmentLines(spec, font);
  return {
    id: "missions/assignment",
    chrome: { kind: "panel", title: "MISSION ASSIGNMENT", emblem: "roundel", background: undefined },
    widgets: [
      { kind: "text", text: title, x: 161 - Math.ceil((measureText(font, title) - 1) / 2), y: ASSIGNMENT_HEADING_Y, role: "prose" },
      ...assignmentBody(lines, assignmentStartLabel(spec), font),
      ...assignmentUnderlines(lines, font),
      // FONT1, because `boardButtonOps` draws every button label in FONT1. The
      // body atlas used to be passed here and the four labels came out 4 to 13
      // pixels wider than the buttons measured for them.
      ...actionRow(actions, heading),
    ],
    cancel: actions[0]?.action,
  };
}

/**
 * FLIGHT ASSIGNMENTS lives in its own module (`flight-assignments-screen.ts`) —
 * it is the largest of these four screens and G34 gave it a formation thumbnail
 * to draw. Re-exported here so every existing caller keeps its import path.
 */
export {
  FORMATION_WELL,
  ROSTER_PLATE,
  THUMBNAIL_FILL,
  flightAssignmentsScreen,
  formationMarks,
  formationOf,
  skillLabelOf,
  type FormationMark,
} from "./flight-assignments-screen.ts";
